// src/components/booking/forms/ListingForm.jsx
import React, { useState, useEffect } from 'react';
import { 
  Save, 
  X, 
  Phone,
  Mail,
  Upload, 
  MapPin, 
  DollarSign, 
  Users, 
  Calendar,
  Clock,
  Plus,
  Trash2,
  Star,
  Camera,
  Image as ImageIcon,
  Settings,
  Bed,
  Car,
  Building2,
  Wifi,
  Coffee,
  Tv,
  AirVent,
  ParkingCircle,
  UtensilsCrossed,
  Waves,
  TreePine,
  Dumbbell,
  Shield,
  Video,
  Heart,
  AlertCircle,
  CheckCircle,
  Edit3,
  Link,
  Globe
} from 'lucide-react';
import { FileUpload } from '../../common/FileUpload';
import { bookingService } from '../../../services/bookingService';
import { firestoreService } from '../../../services/firestoreService';
import { MediaTab } from './MediaTab';
import { ListingInteractionsTab } from './ListingInteractionsTab';

const BOOKING_TYPES = {
  accommodation: {
    label: 'Accommodation',
    icon: Bed,
    color: '#3b82f6',
    defaultDuration: 'nights',
    amenities: [
      { id: 'wifi', label: 'Free WiFi', icon: Wifi },
      { id: 'parking', label: 'Free Parking', icon: ParkingCircle },
      { id: 'breakfast', label: 'Breakfast Included', icon: Coffee },
      { id: 'ac', label: 'Air Conditioning', icon: AirVent },
      { id: 'tv', label: 'TV', icon: Tv },
      { id: 'kitchen', label: 'Kitchen', icon: UtensilsCrossed },
      { id: 'pool', label: 'Swimming Pool', icon: Waves },
      { id: 'gym', label: 'Fitness Center', icon: Dumbbell },
      { id: 'petfriendly', label: 'Pet Friendly', icon: TreePine },
      { id: 'security', label: '24/7 Security', icon: Shield }
    ]
  },
  appointment: {
    label: 'Appointments',
    icon: Clock,
    color: '#10b981',
    defaultDuration: 'minutes',
    amenities: [
      { id: 'parking', label: 'Free Parking', icon: ParkingCircle },
      { id: 'wifi', label: 'Free WiFi', icon: Wifi },
      { id: 'coffee', label: 'Complimentary Refreshments', icon: Coffee },
      { id: 'ac', label: 'Climate Controlled', icon: AirVent },
      { id: 'wheelchair', label: 'Wheelchair Accessible', icon: Shield },
      { id: 'online', label: 'Online Consultation Available', icon: Globe }
    ]
  },
  rental: {
    label: 'Rentals',
    icon: Car,
    color: '#f59e0b',
    defaultDuration: 'days',
    amenities: [
      { id: 'insurance', label: 'Insurance Included', icon: Shield },
      { id: 'delivery', label: 'Free Delivery', icon: Car },
      { id: 'support', label: '24/7 Support', icon: Clock },
      { id: 'gps', label: 'GPS Navigation', icon: MapPin },
      { id: 'ac', label: 'Air Conditioning', icon: AirVent }
    ]
  },
  venue: {
    label: 'Venues',
    icon: Building2,
    color: '#8b5cf6',
    defaultDuration: 'hours',
    amenities: [
      { id: 'parking', label: 'Parking Available', icon: ParkingCircle },
      { id: 'catering', label: 'Catering Services', icon: UtensilsCrossed },
      { id: 'av', label: 'A/V Equipment', icon: Tv },
      { id: 'wifi', label: 'High-Speed WiFi', icon: Wifi },
      { id: 'ac', label: 'Climate Control', icon: AirVent },
      { id: 'security', label: 'Security Services', icon: Shield }
    ]
  }
};

const CANCELLATION_POLICIES = [
  { value: 'flexible', label: 'Flexible - Free cancellation 24 hours before' },
  { value: 'moderate', label: 'Moderate - Free cancellation 5 days before' },
  { value: 'strict', label: 'Strict - 50% refund up to 1 week before' },
  { value: 'super-strict', label: 'Super Strict - No refunds' }
];

export const ListingForm = ({ 
 bookId, 
  listing = null, 
  bookingType = 'accommodation',
  businessModel = 'single',
  vendors = [],
  currentVendorId = null,
  onSave, 
  onCancel 
}) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({
    // Basic Information
    title: '',
    description: '',
    price: '',
    currency: 'USD',
    location: '',
    address: '',
    coordinates: { lat: null, lng: null },
    maxGuests: 1,
    status: 'active',
    vendorId: currentVendorId || null,
    
    // Media
    images: [],
    videos: [],
    virtualTourUrl: '',
    
    // Amenities & Features
    amenities: [],
    customAmenities: [],
    highlights: [],
    
    // Type-Specific Fields
    rooms: 1,
    bathrooms: 1,
    bedrooms: 1,
    checkInTime: '15:00',
    checkOutTime: '11:00',
    propertyType: 'apartment',
    
    duration: 60,
    serviceType: '',
    practitioner: '',
    category: '',
    
    pickupLocation: '',
    returnLocation: '',
    insuranceRequired: false,
    vehicleType: '',
    
    capacity: 50,
    cateringAvailable: false,
    setupOptions: [],
    venueType: 'conference-room',
    
    // Policies & Rules
    cancellationPolicy: 'flexible',
    houseRules: [],
    checkInInstructions: '',
    safetyFeatures: [],
    
    // Pricing & Availability
    weekendPricing: false,
    weekendMultiplier: 1.2,
    minimumStay: 1,
    maximumStay: 30,
    instantBooking: false,
    
    // Contact & Booking
    contactPhone: '',
    contactEmail: '',
    website: '',
    externalBookingUrl: '',
    
    // SEO & Marketing
    tags: [],
    featured: false,
    promotionText: '',
    seoTitle: '',
    seoDescription: ''
  });

  const [uploadStates, setUploadStates] = useState({});
  const [uploadErrors, setUploadErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [newTag, setNewTag] = useState('');
  const [newRule, setNewRule] = useState('');

  const currentBookingType = BOOKING_TYPES[bookingType];


  useEffect(() => {
    if (listing) {
      setFormData({
        title: listing.title || '',
        description: listing.description || '',
        price: listing.price || '',
        currency: listing.currency || 'USD',
        location: listing.location || '',
        address: listing.address || '',
        coordinates: listing.coordinates || { lat: null, lng: null },
        maxGuests: listing.maxGuests || 1,
        status: listing.status || 'active',
        vendorId: listing.vendorId || currentVendorId || null,
        
        images: listing.images || [],
        videos: listing.videos || [],
        virtualTourUrl: listing.virtualTourUrl || '',
        
        amenities: listing.amenities || [],
        customAmenities: listing.customAmenities || [],
        highlights: listing.highlights || [],
        
        // Type-specific fields with fallbacks
        rooms: listing.rooms || 1,
        bathrooms: listing.bathrooms || 1,
        bedrooms: listing.bedrooms || 1,
        checkInTime: listing.checkInTime || '15:00',
        checkOutTime: listing.checkOutTime || '11:00',
        propertyType: listing.propertyType || 'apartment',
        
        duration: listing.duration || 60,
        serviceType: listing.serviceType || '',
        practitioner: listing.practitioner || '',
        category: listing.category || '',
        
        pickupLocation: listing.pickupLocation || '',
        returnLocation: listing.returnLocation || '',
        insuranceRequired: listing.insuranceRequired || false,
        vehicleType: listing.vehicleType || '',
        
        capacity: listing.capacity || 50,
        cateringAvailable: listing.cateringAvailable || false,
        setupOptions: listing.setupOptions || [],
        venueType: listing.venueType || 'conference-room',
        
        cancellationPolicy: listing.cancellationPolicy || 'flexible',
        houseRules: listing.houseRules || [],
        checkInInstructions: listing.checkInInstructions || '',
        safetyFeatures: listing.safetyFeatures || [],
        
        weekendPricing: listing.weekendPricing || false,
        weekendMultiplier: listing.weekendMultiplier || 1.2,
        minimumStay: listing.minimumStay || 1,
        maximumStay: listing.maximumStay || 30,
        instantBooking: listing.instantBooking || false,
        
        contactPhone: listing.contactPhone || '',
        contactEmail: listing.contactEmail || '',
        website: listing.website || '',
        externalBookingUrl: listing.externalBookingUrl || '',
        
        tags: listing.tags || [],
        featured: listing.featured || false,
        promotionText: listing.promotionText || '',
        seoTitle: listing.seoTitle || '',
        seoDescription: listing.seoDescription || ''
      });
    }
  }, [listing]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear errors
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleImageUpload = async (file, error) => {
    if (error) {
      setUploadErrors(prev => ({ ...prev, images: error }));
      return;
    }

    const uploadId = Date.now();
    setUploadStates(prev => ({ ...prev, [`image-${uploadId}`]: true }));
    setUploadErrors(prev => ({ ...prev, images: '' }));

    try {
      const result = await bookingService.uploadListingMedia(
        file, 
        bookId, 
        listing?.id || 'temp', 
        'images'
      );
      
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, {
          id: uploadId,
          url: result.url,
          fileName: result.fileName,
          originalName: file.name,
          alt: '',
          featured: prev.images.length === 0 // First image is featured
        }]
      }));
    } catch (error) {
      console.error('Error uploading image:', error);
      setUploadErrors(prev => ({ ...prev, images: error.message }));
    } finally {
      setUploadStates(prev => ({ ...prev, [`image-${uploadId}`]: false }));
    }
  };

  const removeImage = async (index) => {
    const image = formData.images[index];
    
    try {
      await bookingService.deleteListingMedia(image.url);
      
      setFormData(prev => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== index)
      }));
    } catch (error) {
      console.error('Error removing image:', error);
    }
  };

  const updateImageDetails = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.map((img, i) => 
        i === index ? { ...img, [field]: value } : img
      )
    }));
  };

  const toggleAmenity = (amenityId) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenityId)
        ? prev.amenities.filter(id => id !== amenityId)
        : [...prev.amenities, amenityId]
    }));
  };

  const addCustomAmenity = () => {
    if (newTag.trim() && !formData.customAmenities.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        customAmenities: [...prev.customAmenities, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const addHouseRule = () => {
    if (newRule.trim() && !formData.houseRules.includes(newRule.trim())) {
      setFormData(prev => ({
        ...prev,
        houseRules: [...prev.houseRules, newRule.trim()]
      }));
      setNewRule('');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.price || formData.price <= 0) {
      newErrors.price = 'Valid price is required';
    }

    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }

    if (formData.images.length === 0) {
      newErrors.images = 'At least one image is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      setActiveTab('basic'); // Switch to basic tab to show errors
      return;
    }

    setSaving(true);
    try {
      const listingData = {
        ...formData,
        price: parseFloat(formData.price) || 0,
        maxGuests: parseInt(formData.maxGuests) || 1,
        updatedAt: new Date()
      };

      if (listing) {
        await bookingService.updateBookingListing(bookId, listing.id, listingData);
      } else {
        await bookingService.createBookingListing(bookId, listingData);
      }

      onSave();
    } catch (error) {
      console.error('Error saving listing:', error);
      alert('Failed to save listing: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: Edit3 },
    { id: 'media', label: 'Photos & Media', icon: Camera },
    { id: 'amenities', label: 'Amenities', icon: Star },
    { id: 'details', label: 'Details', icon: Settings },
    { id: 'interactions', label: 'Interactions', icon: Heart }, // Add this
    { id: 'policies', label: 'Policies', icon: Shield },
    { id: 'pricing', label: 'Pricing', icon: DollarSign },
    { id: 'seo', label: 'SEO & Marketing', icon: Globe }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div 
          className="text-white p-6 flex items-center justify-between"
          style={{ backgroundColor: currentBookingType.color }}
        >
          <div className="flex items-center gap-3">
            {React.createElement(currentBookingType.icon, { className: "h-6 w-6" })}
            <div>
              <h2 className="text-xl font-semibold">
                {listing ? 'Edit' : 'Create'} {currentBookingType.label} Listing
              </h2>
              <p className="text-white/80 text-sm">
                Fill in the details to create an amazing listing
              </p>
            </div>
          </div>
          <button 
            onClick={onCancel} 
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-6">
          <nav className="-mb-px flex space-x-6 overflow-x-auto">
            {tabs.map(tab => {
              const TabIcon = tab.icon;
              const hasError = tab.id === 'basic' && (errors.title || errors.description || errors.price || errors.location);
              const hasMediaError = tab.id === 'media' && errors.images;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-2 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <TabIcon className="h-4 w-4" />
                  {tab.label}
                  {(hasError || hasMediaError) && (
                    <AlertCircle className="h-3 w-3 text-red-500" />
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(95vh-200px)]">
          {activeTab === 'basic' && (
            <BasicInfoTab 
            formData={formData}
            handleInputChange={handleInputChange}
            errors={errors}
            bookingType={bookingType}
            currentBookingType={currentBookingType}
            businessModel={businessModel}
            vendors={vendors}
          />
          )}

          {activeTab === 'media' && (
            <MediaTab 
              formData={formData}
              handleInputChange={handleInputChange}
              handleImageUpload={handleImageUpload}
              removeImage={removeImage}
              updateImageDetails={updateImageDetails}
              uploadStates={uploadStates}
              uploadErrors={uploadErrors}
              errors={errors}
              bookId={bookId} // Add this prop
            />
          )}

          {activeTab === 'amenities' && (
            <AmenitiesTab 
              formData={formData}
              handleInputChange={handleInputChange}
              toggleAmenity={toggleAmenity}
              addCustomAmenity={addCustomAmenity}
              newTag={newTag}
              setNewTag={setNewTag}
              currentBookingType={currentBookingType}
            />
          )}

          {activeTab === 'details' && (
            <DetailsTab 
              formData={formData}
              handleInputChange={handleInputChange}
              bookingType={bookingType}
            />
          )}

          {activeTab === 'policies' && (
            <PoliciesTab 
              formData={formData}
              handleInputChange={handleInputChange}
              addHouseRule={addHouseRule}
              newRule={newRule}
              setNewRule={setNewRule}
            />
          )}

          {activeTab === 'pricing' && (
            <PricingTab 
              formData={formData}
              handleInputChange={handleInputChange}
              currentBookingType={currentBookingType}
            />
          )}

          {activeTab === 'seo' && (
            <SEOTab 
              formData={formData}
              handleInputChange={handleInputChange}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <AlertCircle className="h-4 w-4" />
            Make sure to fill in all required fields and upload at least one image
          </div>
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 text-white rounded-lg flex items-center gap-2 disabled:opacity-50 transition-colors"
              style={{ backgroundColor: currentBookingType.color }}
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {listing ? 'Update Listing' : 'Create Listing'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const BasicInfoTab = ({ formData, handleInputChange, errors, bookingType, currentBookingType, businessModel, vendors }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Title */}
      <div className="lg:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Listing Title *
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => handleInputChange('title', e.target.value)}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            errors.title ? 'border-red-300' : 'border-gray-300'
          }`}
          placeholder="Enter a descriptive title for your listing"
        />
        {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
      </div>

      {/* Vendor Selection - Only in Multi-Vendor Mode */}
      {businessModel === 'multi' && vendors && vendors.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Building2 className="h-6 w-6 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Vendor Assignment</h3>
          </div>
          
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Vendor *
          </label>
          <select
            value={formData.vendorId || ''}
            onChange={(e) => handleInputChange('vendorId', e.target.value || null)}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.vendorId ? 'border-red-300' : 'border-gray-300'
            }`}
          >
            <option value="">Select a vendor...</option>
            {vendors.filter(v => v.status === 'approved').map(vendor => (
              <option key={vendor.id} value={vendor.id}>
                {vendor.name} - {vendor.category}
              </option>
            ))}
          </select>
          {errors.vendorId && <p className="text-red-500 text-sm mt-1">{errors.vendorId}</p>}
          <p className="text-sm text-gray-600 mt-2">
            This listing will be associated with the selected vendor in the marketplace.
          </p>
        </div>
      )}

      {/* Description */}
      <div className="lg:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description *
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          rows={4}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            errors.description ? 'border-red-300' : 'border-gray-300'
          }`}
          placeholder="Describe your listing in detail..."
        />
        {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
      </div>

      {/* Price */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Price *
        </label>
        <div className="relative">
          <span className="absolute left-3 top-3 text-gray-500">R</span>
          <input
            type="number"
            value={formData.price}
            onChange={(e) => handleInputChange('price', e.target.value)}
            className={`w-full pl-8 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.price ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="0"
            min="0"
            step="0.01"
          />
        </div>
        {errors.price && <p className="text-red-500 text-sm mt-1">{errors.price}</p>}
      </div>

      {/* Currency */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Currency
        </label>
        <select
          value={formData.currency}
          onChange={(e) => handleInputChange('currency', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="ZAR">ZAR (R)</option>
          <option value="USD">USD ($)</option>
          <option value="EUR">EUR (€)</option>
          <option value="GBP">GBP (£)</option>
        </select>
      </div>

      {/* Location */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Location *
        </label>
        <input
          type="text"
          value={formData.location}
          onChange={(e) => handleInputChange('location', e.target.value)}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            errors.location ? 'border-red-300' : 'border-gray-300'
          }`}
          placeholder="City, Province/State"
        />
        {errors.location && <p className="text-red-500 text-sm mt-1">{errors.location}</p>}
      </div>

      {/* Max Guests */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Maximum Guests
        </label>
        <input
          type="number"
          value={formData.maxGuests}
          onChange={(e) => handleInputChange('maxGuests', parseInt(e.target.value) || 1)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          min="1"
          max="50"
        />
      </div>
    </div>

    {/* Featured Listing Section */}
    <div className="border-t border-gray-200 pt-6">
      <div className="flex items-center gap-3 mb-4">
        <Star className="h-5 w-5 text-yellow-500" />
        <h3 className="text-lg font-semibold text-gray-900">Featured Listing</h3>
      </div>
      
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
        <div className="flex items-start gap-3">
          <Star className="h-5 w-5 text-yellow-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-yellow-800 mb-1">Boost Your Listing Visibility</h4>
            <p className="text-sm text-yellow-700">
              Featured listings appear in the hero slideshow and get priority placement in search results.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="isFeatured"
            checked={formData.isFeatured || false}
            onChange={(e) => handleInputChange('isFeatured', e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="isFeatured" className="ml-2 block text-sm text-gray-900">
            Make this listing featured
          </label>
        </div>

        {formData.isFeatured && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Featured Period (Days)
              </label>
              <select
                value={formData.featuredPeriod || 30}
                onChange={(e) => handleInputChange('featuredPeriod', parseInt(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={7}>7 Days</option>
                <option value={14}>14 Days</option>
                <option value={30}>30 Days</option>
                <option value={60}>60 Days</option>
                <option value={90}>90 Days</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Featured Priority
              </label>
              <select
                value={formData.featuredPriority || 'normal'}
                onChange={(e) => handleInputChange('featuredPriority', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="normal">Normal Priority</option>
                <option value="high">High Priority</option>
                <option value="urgent">Urgent Priority</option>
              </select>
            </div>

            {/* Featured Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Featured Start Date
              </label>
              <input
                type="date"
                value={formData.featuredStartDate || new Date().toISOString().split('T')[0]}
                onChange={(e) => handleInputChange('featuredStartDate', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Auto-calculated end date display */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Featured End Date
              </label>
              <div className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600">
                {formData.featuredStartDate && formData.featuredPeriod ? 
                  new Date(new Date(formData.featuredStartDate).getTime() + (formData.featuredPeriod * 24 * 60 * 60 * 1000)).toISOString().split('T')[0] : 
                  'Select start date and period'
                }
              </div>
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Status */}
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Listing Status
      </label>
      <select
        value={formData.status}
        onChange={(e) => handleInputChange('status', e.target.value)}
        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        <option value="active">Active</option>
        <option value="inactive">Inactive</option>
        <option value="draft">Draft</option>
        <option value="pending">Pending Review</option>
      </select>
    </div>
  </div>
);

// Amenities Tab Component  
const AmenitiesTab = ({ 
  formData, 
  handleInputChange, 
  toggleAmenity, 
  addCustomAmenity,
  newTag,
  setNewTag,
  currentBookingType 
}) => (
  <div className="space-y-8">
    {/* Standard Amenities */}
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        {currentBookingType.label} Amenities
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {currentBookingType.amenities.map(amenity => {
          const AmenityIcon = amenity.icon;
          const isSelected = formData.amenities.includes(amenity.id);
          
          return (
            <div
              key={amenity.id}
              onClick={() => toggleAmenity(amenity.id)}
              className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                isSelected 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3">
                <AmenityIcon className={`h-5 w-5 ${
                  isSelected ? 'text-blue-600' : 'text-gray-400'
                }`} />
                <span className={`font-medium ${
                  isSelected ? 'text-blue-900' : 'text-gray-700'
                }`}>
                  {amenity.label}
                </span>
                {isSelected && (
                  <CheckCircle className="h-5 w-5 text-blue-600 ml-auto" />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>

    {/* Custom Amenities */}
    <div className="border-t pt-8">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Custom Amenities</h3>
      
      <div className="flex gap-2 mb-4">
        <input
          type="text"
          value={newTag}
          onChange={(e) => setNewTag(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && addCustomAmenity()}
          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          placeholder="Add custom amenity (e.g., 'Ocean view', 'Pet grooming')"
        />
        <button
          onClick={addCustomAmenity}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add
        </button>
      </div>

      {formData.customAmenities.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {formData.customAmenities.map((amenity, index) => (
            <div
              key={index}
              className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-full"
            >
              <span className="text-sm">{amenity}</span>
              <button
                onClick={() => {
                  const updated = formData.customAmenities.filter((_, i) => i !== index);
                  handleInputChange('customAmenities', updated);
                }}
                className="text-gray-500 hover:text-red-500"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>

    {/* Highlights */}
    <div className="border-t pt-8">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Highlights</h3>
      <p className="text-sm text-gray-600 mb-4">
        What makes this listing special? Add up to 5 key highlights.
      </p>
      
      <textarea
        value={formData.highlights.join('\n')}
        onChange={(e) => handleInputChange('highlights', e.target.value.split('\n').filter(h => h.trim()))}
        rows={5}
        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        placeholder="Enter each highlight on a new line:
- Stunning city skyline views
- Recently renovated kitchen
- Walking distance to metro station
- Rooftop terrace access
- 24/7 concierge service"
      />
    </div>
  </div>
);

// Details Tab Component
const DetailsTab = ({ formData, handleInputChange, bookingType }) => (
  <div className="space-y-8">
    {/* Type-Specific Details */}
    {bookingType === 'accommodation' && (
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Bed className="h-5 w-5 text-blue-500" />
          Accommodation Details
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bedrooms
            </label>
            <input
              type="number"
              value={formData.bedrooms}
              onChange={(e) => handleInputChange('bedrooms', parseInt(e.target.value) || 1)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              min="1"
              max="20"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bathrooms
            </label>
            <input
              type="number"
              value={formData.bathrooms}
              onChange={(e) => handleInputChange('bathrooms', parseInt(e.target.value) || 1)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              min="1"
              max="10"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Check-in Time
            </label>
            <input
              type="time"
              value={formData.checkInTime}
              onChange={(e) => handleInputChange('checkInTime', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Check-out Time
            </label>
            <input
              type="time"
              value={formData.checkOutTime}
              onChange={(e) => handleInputChange('checkOutTime', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            />
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Property Type
          </label>
          <select
            value={formData.propertyType}
            onChange={(e) => handleInputChange('propertyType', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
          >
            <option value="apartment">Apartment</option>
            <option value="house">House</option>
            <option value="villa">Villa</option>
            <option value="condo">Condo</option>
            <option value="hotel">Hotel</option>
            <option value="lodge">Lodge</option>
            <option value="bnb">Bed & Breakfast</option>
            <option value="cabin">Cabin</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>
    )}

    {bookingType === 'appointment' && (
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Clock className="h-5 w-5 text-green-500" />
          Appointment Details
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Duration (minutes)
            </label>
            <input
              type="number"
              value={formData.duration}
              onChange={(e) => handleInputChange('duration', parseInt(e.target.value) || 60)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              min="15"
              step="15"
              max="480"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Service Category
            </label>
            <select
              value={formData.category}
              onChange={(e) => handleInputChange('category', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">Select category</option>
              <option value="medical">Medical</option>
              <option value="dental">Dental</option>
              <option value="beauty">Beauty & Spa</option>
              <option value="fitness">Fitness</option>
              <option value="consultation">Consultation</option>
              <option value="therapy">Therapy</option>
              <option value="legal">Legal</option>
              <option value="financial">Financial</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Service Type
            </label>
            <input
              type="text"
              value={formData.serviceType}
              onChange={(e) => handleInputChange('serviceType', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              placeholder="e.g., Consultation, Treatment"
            />
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Practitioner/Provider
          </label>
          <input
            type="text"
            value={formData.practitioner}
            onChange={(e) => handleInputChange('practitioner', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            placeholder="Dr. Smith, Sarah Johnson, etc."
          />
        </div>
      </div>
    )}

    {bookingType === 'rental' && (
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Car className="h-5 w-5 text-orange-500" />
          Rental Details
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Vehicle/Item Type
            </label>
            <select
              value={formData.vehicleType}
              onChange={(e) => handleInputChange('vehicleType', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">Select type</option>
              <option value="car">Car</option>
              <option value="motorcycle">Motorcycle</option>
              <option value="van">Van</option>
              <option value="truck">Truck</option>
              <option value="bicycle">Bicycle</option>
              <option value="equipment">Equipment</option>
              <option value="tools">Tools</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Pickup Location
            </label>
            <input
              type="text"
              value={formData.pickupLocation}
              onChange={(e) => handleInputChange('pickupLocation', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              placeholder="Pickup address or location"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Return Location
            </label>
            <input
              type="text"
              value={formData.returnLocation}
              onChange={(e) => handleInputChange('returnLocation', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              placeholder="Return address or location"
            />
          </div>

          <div className="flex items-center pt-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.insuranceRequired}
                onChange={(e) => handleInputChange('insuranceRequired', e.target.checked)}
                className="mr-3 h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
              />
              <span className="text-sm font-medium text-gray-700">Insurance Required</span>
            </label>
          </div>
        </div>
      </div>
    )}

    {bookingType === 'venue' && (
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Building2 className="h-5 w-5 text-purple-500" />
          Venue Details
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Venue Type
            </label>
            <select
              value={formData.venueType}
              onChange={(e) => handleInputChange('venueType', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            >
              <option value="conference-room">Conference Room</option>
              <option value="meeting-room">Meeting Room</option>
              <option value="event-hall">Event Hall</option>
              <option value="wedding-venue">Wedding Venue</option>
              <option value="studio">Studio</option>
              <option value="warehouse">Warehouse</option>
              <option value="outdoor-space">Outdoor Space</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Capacity (people)
            </label>
            <input
              type="number"
              value={formData.capacity}
              onChange={(e) => handleInputChange('capacity', parseInt(e.target.value) || 50)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              min="1"
              max="10000"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Min Duration (hours)
            </label>
            <input
              type="number"
              value={formData.duration / 60}
              onChange={(e) => handleInputChange('duration', (parseInt(e.target.value) || 1) * 60)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              min="1"
              max="24"
            />
          </div>
        </div>

        <div className="mt-4 flex items-center gap-6">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.cateringAvailable}
              onChange={(e) => handleInputChange('cateringAvailable', e.target.checked)}
              className="mr-3 h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
            />
            <span className="text-sm font-medium text-gray-700">Catering Available</span>
          </label>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Setup Options
          </label>
          <textarea
            value={formData.setupOptions.join('\n')}
            onChange={(e) => handleInputChange('setupOptions', e.target.value.split('\n').filter(s => s.trim()))}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            placeholder="Enter setup options (one per line):
Theater style
Classroom setup
U-shape configuration
Cocktail setup"
          />
        </div>
      </div>
    )}

    {/* Contact Information */}
    <div className="bg-white p-6 rounded-lg border border-gray-200">
      <h3 className="text-lg font-semibold mb-4">Contact & Booking Information</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Phone className="h-4 w-4 inline mr-1" />
            Contact Phone
          </label>
          <input
            type="tel"
            value={formData.contactPhone}
            onChange={(e) => handleInputChange('contactPhone', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            placeholder="+1 (555) 123-4567"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Mail className="h-4 w-4 inline mr-1" />
            Contact Email
          </label>
          <input
            type="email"
            value={formData.contactEmail}
            onChange={(e) => handleInputChange('contactEmail', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            placeholder="contact@example.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Globe className="h-4 w-4 inline mr-1" />
            Website
          </label>
          <input
            type="url"
            value={formData.website}
            onChange={(e) => handleInputChange('website', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            placeholder="https://website.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Link className="h-4 w-4 inline mr-1" />
            External Booking URL
          </label>
          <input
            type="url"
            value={formData.externalBookingUrl}
            onChange={(e) => handleInputChange('externalBookingUrl', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            placeholder="https://booking-system.com/reserve"
          />
        </div>
      </div>
    </div>
  </div>
);

// Policies Tab Component
const PoliciesTab = ({ formData, handleInputChange, addHouseRule, newRule, setNewRule }) => (
  <div className="space-y-8">
    {/* Cancellation Policy */}
    <div className="bg-white p-6 rounded-lg border border-gray-200">
      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <Shield className="h-5 w-5 text-blue-500" />
        Cancellation Policy
      </h3>
      
      <div className="space-y-4">
        {CANCELLATION_POLICIES.map(policy => (
          <div
            key={policy.value}
            onClick={() => handleInputChange('cancellationPolicy', policy.value)}
            className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
              formData.cancellationPolicy === policy.value
                ? 'border-emerald-500 bg-emerald-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-4 h-4 rounded-full border-2 ${
                formData.cancellationPolicy === policy.value
                  ? 'border-emerald-500 bg-emerald-500'
                  : 'border-gray-300'
              }`}>
                {formData.cancellationPolicy === policy.value && (
                  <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>
                )}
              </div>
              <div>
                <div className="font-medium text-gray-900">{policy.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* House Rules */}
    <div className="bg-white p-6 rounded-lg border border-gray-200">
      <h3 className="text-lg font-semibold mb-4">House Rules & Guidelines</h3>
      
      <div className="space-y-4">
        <div className="flex gap-2">
          <input
            type="text"
            value={newRule}
            onChange={(e) => setNewRule(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addHouseRule()}
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            placeholder="Add a house rule (e.g., 'No smoking', 'Quiet hours after 10 PM')"
          />
          <button
            onClick={addHouseRule}
            className="px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Rule
          </button>
        </div>

        {formData.houseRules.length > 0 && (
          <div className="space-y-2">
            {formData.houseRules.map((rule, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <span className="text-sm text-gray-700">{rule}</span>
                <button
                  onClick={() => {
                    const updated = formData.houseRules.filter((_, i) => i !== index);
                    handleInputChange('houseRules', updated);
                  }}
                  className="text-red-500 hover:text-red-700"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {formData.houseRules.length === 0 && (
          <div className="text-center py-6 border-2 border-dashed border-gray-300 rounded-lg">
            <p className="text-gray-500">No house rules added yet</p>
            <p className="text-sm text-gray-400 mt-1">Add rules to set clear expectations for guests</p>
          </div>
        )}
      </div>
    </div>

    {/* Check-in Instructions */}
    <div className="bg-white p-6 rounded-lg border border-gray-200">
      <h3 className="text-lg font-semibold mb-4">Check-in Instructions</h3>
      
      <textarea
        value={formData.checkInInstructions}
        onChange={(e) => handleInputChange('checkInInstructions', e.target.value)}
        rows={6}
        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
        placeholder="Provide detailed check-in instructions for guests:
- How to access the property
- Where to find keys
- WiFi password
- Important contact information
- Any special instructions"
      />
      <p className="text-sm text-gray-500 mt-2">
        These instructions will be sent to guests upon booking confirmation
      </p>
    </div>

    {/* Safety Features */}
    <div className="bg-white p-6 rounded-lg border border-gray-200">
      <h3 className="text-lg font-semibold mb-4">Safety Features</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          'Smoke detector',
          'Carbon monoxide detector',
          'Fire extinguisher',
          'First aid kit',
          'Security cameras',
          'Deadbolt locks',
          'Safe/lockbox',
          'Emergency lighting'
        ].map(feature => (
          <label key={feature} className="flex items-center">
            <input
              type="checkbox"
              checked={formData.safetyFeatures.includes(feature)}
              onChange={(e) => {
                const updated = e.target.checked
                  ? [...formData.safetyFeatures, feature]
                  : formData.safetyFeatures.filter(f => f !== feature);
                handleInputChange('safetyFeatures', updated);
              }}
              className="mr-3 h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-700">{feature}</span>
          </label>
        ))}
      </div>
    </div>
  </div>
);

// Pricing Tab Component
const PricingTab = ({ formData, handleInputChange, currentBookingType }) => (
  <div className="space-y-8">
    {/* Base Pricing */}
    <div className="bg-white p-6 rounded-lg border border-gray-200">
      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <DollarSign className="h-5 w-5 text-green-500" />
        Base Pricing
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Base Price per {currentBookingType.defaultDuration.slice(0, -1)}
          </label>
          <div className="flex">
            <span className="inline-flex items-center px-3 py-2 border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm rounded-l-lg">
              {formData.currency}
            </span>
            <input
              type="number"
              value={formData.price}
              onChange={(e) => handleInputChange('price', e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-emerald-500"
              placeholder="0.00"
              min="0"
              step="0.01"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Minimum Stay/Duration
          </label>
          <div className="flex">
            <input
              type="number"
              value={formData.minimumStay}
              onChange={(e) => handleInputChange('minimumStay', parseInt(e.target.value) || 1)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-emerald-500"
              min="1"
            />
            <span className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 bg-gray-50 text-gray-500 text-sm rounded-r-lg">
              {currentBookingType.defaultDuration}
            </span>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Maximum Stay/Duration
          </label>
          <div className="flex">
            <input
              type="number"
              value={formData.maximumStay}
              onChange={(e) => handleInputChange('maximumStay', parseInt(e.target.value) || 30)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-emerald-500"
              min="1"
            />
            <span className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 bg-gray-50 text-gray-500 text-sm rounded-r-lg">
              {currentBookingType.defaultDuration}
            </span>
          </div>
        </div>
      </div>
    </div>

    {/* Weekend Pricing */}
    {(formData.bookingType === 'accommodation' || formData.bookingType === 'venue') && (
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold mb-4">Weekend Pricing</h3>
        
        <div className="space-y-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.weekendPricing}
              onChange={(e) => handleInputChange('weekendPricing', e.target.checked)}
              className="mr-3 h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
            />
            <span className="text-sm font-medium text-gray-700">Enable weekend pricing</span>
          </label>

          {formData.weekendPricing && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Weekend Multiplier
                </label>
                <input
                  type="number"
                  value={formData.weekendMultiplier}
                  onChange={(e) => handleInputChange('weekendMultiplier', parseFloat(e.target.value) || 1.2)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  min="1"
                  max="5"
                  step="0.1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Weekend price: ${((formData.price || 0) * (formData.weekendMultiplier || 1.2)).toFixed(2)}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Weekend Days
                </label>
                <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                  Friday - Sunday
                  <div className="text-xs text-gray-500 mt-1">
                    Weekend pricing applies to Friday check-ins through Sunday
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    )}

    {/* Booking Settings */}
    <div className="bg-white p-6 rounded-lg border border-gray-200">
      <h3 className="text-lg font-semibold mb-4">Booking Settings</h3>
      
      <div className="space-y-6">
        <label className="flex items-center justify-between">
          <div>
            <span className="text-sm font-medium text-gray-700">Instant Booking</span>
            <p className="text-xs text-gray-500">Guests can book immediately without approval</p>
          </div>
          <div className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.instantBooking}
              onChange={(e) => handleInputChange('instantBooking', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
          </div>
        </label>

        <div className="border-t pt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Promotion Text (Optional)
          </label>
          <input
            type="text"
            value={formData.promotionText}
            onChange={(e) => handleInputChange('promotionText', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            placeholder="e.g., 'Book now and save 20%', 'Early bird special'"
          />
          <p className="text-xs text-gray-500 mt-1">
            This text will be displayed prominently on your listing
          </p>
        </div>
      </div>
    </div>

    {/* Pricing Summary */}
    <div className="bg-emerald-50 p-6 rounded-lg border border-emerald-200">
      <h3 className="text-lg font-semibold text-emerald-800 mb-4">Pricing Summary</h3>
      
      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-emerald-700">Base price:</span>
          <span className="font-medium text-emerald-800">
            {formData.currency} {formData.price || 0} per {currentBookingType.defaultDuration.slice(0, -1)}
          </span>
        </div>
        
        {formData.weekendPricing && (
          <div className="flex justify-between">
            <span className="text-emerald-700">Weekend price:</span>
            <span className="font-medium text-emerald-800">
              {formData.currency} {((formData.price || 0) * (formData.weekendMultiplier || 1.2)).toFixed(2)}
            </span>
          </div>
        )}
        
        <div className="flex justify-between pt-2 border-t border-emerald-200">
          <span className="text-emerald-700">Minimum booking:</span>
          <span className="font-medium text-emerald-800">
            {formData.minimumStay || 1} {currentBookingType.defaultDuration}
          </span>
        </div>
      </div>
    </div>
  </div>
);

// SEO Tab Component
const SEOTab = ({ formData, handleInputChange }) => {
  const addTag = (newTag) => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      handleInputChange('tags', [...formData.tags, newTag.trim()]);
    }
  };

  const removeTag = (index) => {
    const updated = formData.tags.filter((_, i) => i !== index);
    handleInputChange('tags', updated);
  };

  return (
    <div className="space-y-8">
      {/* SEO Settings */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Globe className="h-5 w-5 text-blue-500" />
          Search Engine Optimization
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SEO Title
            </label>
            <input
              type="text"
              value={formData.seoTitle}
              onChange={(e) => handleInputChange('seoTitle', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              placeholder="Enter SEO-optimized title (recommended: 50-60 characters)"
              maxLength="60"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>This appears in search engine results</span>
              <span>{formData.seoTitle.length}/60</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SEO Description
            </label>
            <textarea
              value={formData.seoDescription}
              onChange={(e) => handleInputChange('seoDescription', e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              placeholder="Write a compelling description that appears in search results (recommended: 150-160 characters)"
              maxLength="160"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>This appears below the title in search results</span>
              <span>{formData.seoDescription.length}/160</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tags */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold mb-4">Tags & Keywords</h3>
        
        <div className="space-y-4">
          <div className="flex gap-2">
            <input
              type="text"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  addTag(e.target.value);
                  e.target.value = '';
                }
              }}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              placeholder="Add tags (press Enter to add)"
            />
            <button
              onClick={(e) => {
                const input = e.target.previousElementSibling;
                addTag(input.value);
                input.value = '';
              }}
              className="px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Add
            </button>
          </div>

          {formData.tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {formData.tags.map((tag, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm"
                >
                  <span>{tag}</span>
                  <button
                    onClick={() => removeTag(index)}
                    className="text-emerald-600 hover:text-emerald-800"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Suggested Tags:</h4>
            <div className="flex flex-wrap gap-2 text-sm">
              {[
                'luxury', 'budget-friendly', 'family-friendly', 'pet-friendly', 
                'business-travel', 'romantic', 'central-location', 'quiet', 
                'modern', 'traditional', 'eco-friendly', 'accessible'
              ].filter(suggestedTag => !formData.tags.includes(suggestedTag)).slice(0, 8).map(suggestedTag => (
                <button
                  key={suggestedTag}
                  onClick={() => addTag(suggestedTag)}
                  className="px-3 py-1 bg-white border border-gray-300 rounded-full text-gray-600 hover:bg-gray-100 text-xs"
                >
                  + {suggestedTag}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Marketing */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold mb-4">Marketing Features</h3>
        
        <div className="space-y-6">
          <label className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-gray-700">Featured Listing</span>
              <p className="text-xs text-gray-500">Highlight this listing for better visibility</p>
            </div>
            <div className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.featured}
                onChange={(e) => handleInputChange('featured', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
            </div>
          </label>
        </div>
      </div>

      {/* Preview */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold mb-4">Search Result Preview</h3>
        
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="text-blue-600 text-lg hover:underline cursor-pointer">
            {formData.seoTitle || formData.title || 'Your Listing Title'}
          </div>
          <div className="text-green-600 text-sm">
            yoursite.com/listing/{formData.title?.toLowerCase().replace(/\s+/g, '-') || 'listing-url'}
          </div>
          <div className="text-gray-700 text-sm mt-1">
            {formData.seoDescription || formData.description?.slice(0, 160) || 'Your listing description will appear here...'}
          </div>
        </div>
      </div>
    </div>
  );
};